import { FaceletCube } from './facelet_cube.js';
import { visualizeCube } from './visualization.js';
import { RubiksCubeRenderer } from './renderer.js';

export class ControlPanel {
    constructor(container) {
        this.container = container;
        this.isAnimating = false;
        this.showDebugInfo = false; // Track visibility state
        this.moveHistory = []; // Stack to store move history
        
        // Create facelet cube instance
        this.faceletCube = new FaceletCube();
        
        // Create question mark button
        this.createQuestionMarkButton();
        
        // Create control panel container
        this.panel = document.createElement('div');
        this.panel.style.position = 'fixed';
        this.panel.style.bottom = '20px';
        this.panel.style.left = '50%';
        this.panel.style.transform = 'translateX(-50%)';
        this.panel.style.backgroundColor = '#f0f0f0';
        this.panel.style.padding = '10px';
        this.panel.style.borderRadius = '5px';
        this.panel.style.display = 'flex';
        this.panel.style.flexDirection = 'column';
        this.panel.style.gap = '10px';
        this.panel.style.zIndex = '1000';
        this.panel.style.transition = 'all 0.3s ease';
        this.container.appendChild(this.panel);

        // Create signature
        this.createSignature();

        // Create action buttons container
        this.createActionButtons();

        // Create move buttons container
        this.moveButtonsContainer = document.createElement('div');
        this.moveButtonsContainer.style.display = 'flex';
        this.moveButtonsContainer.style.gap = '5px';
        this.panel.appendChild(this.moveButtonsContainer);

        // Add responsive styles
        this.addResponsiveStyles();

        // Create move buttons
        this.createMoveButtons();
        
        // Create state display
        this.createStateDisplay();
        
        // Create 2D visualization
        this.create2DVisualization();
        
        // Create 3D visualization
        this.create3DVisualization();

        // Add keyboard event listener
        this.setupKeyboardControls();

        // Add resize listener
        window.addEventListener('resize', () => this.handleResize());
    }

    createActionButtons() {
        const actionContainer = document.createElement('div');
        actionContainer.style.display = 'flex';
        actionContainer.style.gap = '10px';
        actionContainer.style.justifyContent = 'center';

        // Add keyframes for glowing and burning effects
        const style = document.createElement('style');
        style.textContent = `
            @keyframes glowing {
                0% { box-shadow: 0 0 5px #9C27B0, 0 0 10px #9C27B0, 0 0 15px #9C27B0; }
                50% { box-shadow: 0 0 20px #9C27B0, 0 0 25px #9C27B0, 0 0 30px #9C27B0; }
                100% { box-shadow: 0 0 5px #9C27B0, 0 0 10px #9C27B0, 0 0 15px #9C27B0; }
            }
            
            @keyframes burning {
                0% { box-shadow: 0 0 10px #FF5722, 0 0 20px #FF5722, 0 0 30px #FF9800; }
                50% { box-shadow: 0 0 20px #FF5722, 0 0 30px #FF5722, 0 0 40px #FF9800; }
                100% { box-shadow: 0 0 10px #FF5722, 0 0 20px #FF5722, 0 0 30px #FF9800; }
            }
        `;
        document.head.appendChild(style);

        // Create Scramble button
        const scrambleButton = this.createActionButton('Scramble', '#2196F3');
        scrambleButton.addEventListener('click', () => this.scrambleCube());
        actionContainer.appendChild(scrambleButton);

        // Create Reset button
        const resetButton = this.createActionButton('Reset', '#f44336');
        resetButton.addEventListener('click', () => this.resetCube());
        actionContainer.appendChild(resetButton);

        // Create Solve button
        const solveButton = this.createActionButton('Solve', '#4CAF50');
        solveButton.addEventListener('click', () => this.solveCube());
        actionContainer.appendChild(solveButton);

        // Create Solve+ button
        const solvePlusButton = this.createActionButton('Solve+', '#9C27B0');
        this.solvePlusButton = solvePlusButton; // Store reference for later use
        solvePlusButton.addEventListener('click', () => this.solvePlusCube());
        actionContainer.appendChild(solvePlusButton);

        this.panel.appendChild(actionContainer);
    }

    createActionButton(text, color) {
        const button = document.createElement('button');
        button.textContent = text;
        button.className = 'action-button';
        button.style.padding = '8px 16px';
        button.style.fontSize = '16px';
        button.style.cursor = 'pointer';
        button.style.backgroundColor = color;
        button.style.color = 'white';
        button.style.border = 'none';
        button.style.borderRadius = '5px';
        button.style.transition = 'all 0.2s ease';
        button.style.touchAction = 'manipulation';

        // Add hover effect
        button.addEventListener('mouseenter', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1.05)';
                button.style.opacity = '0.9';
            }
        });

        button.addEventListener('mouseleave', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1)';
                button.style.opacity = '1';
            }
        });

        // Add touch feedback
        button.addEventListener('touchstart', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(0.95)';
            }
        });

        button.addEventListener('touchend', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1)';
                button.style.opacity = '1';
            }
        });

        return button;
    }

    // Helper function to get inverse move
    getInverseMove(move) {
        if (move.endsWith("'")) {
            return move.slice(0, -1); // Remove prime
        } else if (move.endsWith('2')) {
            return move; // Double moves are their own inverse
        } else {
            return move + "'"; // Add prime
        }
    }

    async solveCube() {
        if (this.isAnimating || this.moveHistory.length === 0) return;

        try {
            this.isAnimating = true;
            
            // Process moves in reverse order
            while (this.moveHistory.length > 0) {
                const move = this.moveHistory.pop();
                const inverseMove = this.getInverseMove(move);
                
                // Update internal state
                this.faceletCube.applyMove(inverseMove);
                const newState = this.faceletCube.getFaceletString();
                
                // Store new state in renderer
                this.renderer.currentState = newState;
                
                // Extract face and direction from inverse move
                const face = inverseMove[0];
                const direction = inverseMove.slice(1);
                
                // Execute rotation animation
                if (direction === '2') {
                    await this.renderer.rotateFace(face, 1, true);
                } else {
                    await this.renderer.rotateFace(face, direction === "'" ? -1 : 1, false);
                }
                
                // Update visualizations
                this.updateStateDisplay(newState);
                this.update2DVisualization(newState);
                
                // Small delay between moves
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        } catch (error) {
            console.error('Error solving cube:', error);
        } finally {
            this.isAnimating = false;
        }
    }

    async scrambleCube() {
        if (this.isAnimating) return;

        try {
            this.isAnimating = true;
            const moves = ['U', 'R', 'F', 'D', 'L', 'B'];
            const directions = ['', "'", '2'];
            const numMoves = 20;

            for (let i = 0; i < numMoves; i++) {
                const face = moves[Math.floor(Math.random() * moves.length)];
                const direction = directions[Math.floor(Math.random() * directions.length)];
                const move = face + direction;

                // Record the move in history without clearing it first
                this.moveHistory.push(move);

                // Update internal state
                this.faceletCube.applyMove(move);
                const newState = this.faceletCube.getFaceletString();
                
                // Store new state in renderer
                this.renderer.currentState = newState;
                
                // Execute rotation animation
                if (direction === '2') {
                    await this.renderer.rotateFace(face, 1, true);
                } else {
                    await this.renderer.rotateFace(face, direction === "'" ? -1 : 1, false);
                }
                
                // Update visualizations
                this.updateStateDisplay(newState);
                this.update2DVisualization(newState);
                
                // Small delay between moves
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        } catch (error) {
            console.error('Error during scramble:', error);
        } finally {
            this.isAnimating = false;
        }
    }

    async resetCube() {
        if (this.isAnimating) return;

        try {
            this.isAnimating = true;
            
            // Clear move history
            this.moveHistory = [];
            
            // Reset internal state
            this.faceletCube.reset();
            const newState = this.faceletCube.getFaceletString();
            
            // Update renderer with new state
            await this.renderer.updateCubeState(newState);
            
            // Update visualizations
            this.updateStateDisplay(newState);
            this.update2DVisualization(newState);
            
            // Reset camera position
            this.renderer.camera.position.set(10, 10, 14);
            this.renderer.camera.lookAt(0, 0, 0);
            this.renderer.controls.reset();
            
            // Add a small delay to ensure smooth transition
            await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
            console.error('Error resetting cube:', error);
        } finally {
            this.isAnimating = false;
        }
    }

    // Helper function to parse solution move
    parseSolutionMove(move) {
        // Remove any parentheses and numbers in parentheses
        move = move.replace(/\(\d+[a-z]\)/, '').trim();
        
        // If move is empty after cleaning, skip it
        if (!move) return null;
        
        const face = move[0].toUpperCase(); // Convert to uppercase to handle lowercase moves
        
        // Check if face is valid
        if (!['U', 'R', 'F', 'D', 'L', 'B'].includes(face)) {
            console.log(`Skipping invalid face: ${face}`);
            return null;
        }
        
        let suffix = '';
        
        if (move.length > 1) {
            const modifier = move[1];
            if (modifier === '2') {
                suffix = '2';
            } else if (modifier === '3') {
                suffix = "'"; // Convert 3 to counterclockwise
            }
        }
        
        const parsedMove = face + suffix;
        console.log(`Parsed move ${move} to ${parsedMove}`);
        return parsedMove;
    }

    // Helper function to add glowing effect
    addGlowingEffect(duration = 2000) {
        if (!this.solvePlusButton) return;
        this.solvePlusButton.style.animation = `glowing ${duration}ms infinite`;
        return new Promise(resolve => setTimeout(() => {
            this.solvePlusButton.style.animation = '';
            resolve();
        }, duration));
    }

    // Helper function to add burning effect
    addBurningEffect(duration = 3000) {
        if (!this.solvePlusButton) return;
        this.solvePlusButton.style.animation = `burning ${duration}ms infinite`;
        return new Promise(resolve => setTimeout(() => {
            this.solvePlusButton.style.animation = '';
            resolve();
        }, duration));
    }

    async solvePlusCube() {
        if (this.isAnimating) return;

        try {
            this.isAnimating = true;
            
            // Start glowing effect while fetching solution
            this.addGlowingEffect();
            
            const currentState = this.faceletCube.getFaceletString();
            const response = await fetch('https://stingtao.pythonanywhere.com/solve', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    cubestring: currentState,
                    max_time: 2
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.status === 'success') {
                // Start burning effect and execute moves
                this.addBurningEffect();
                
                // Parse and execute the solution moves
                const moves = data.solution.split(' ').filter(move => move.trim() !== '');
                
                for (const move of moves) {
                    const parsedMove = this.parseSolutionMove(move);
                    if (!parsedMove) continue;
                    
                    console.log(`Executing move: ${parsedMove}`);
                    
                    // Update internal state
                    this.faceletCube.applyMove(parsedMove);
                    const newState = this.faceletCube.getFaceletString();
                    
                    // Store new state in renderer
                    this.renderer.currentState = newState;
                    
                    // Extract face and direction from move
                    const face = parsedMove[0];
                    const direction = parsedMove.slice(1);
                    
                    // Execute rotation animation
                    if (direction === '2') {
                        await this.renderer.rotateFace(face, 1, true);
                    } else {
                        await this.renderer.rotateFace(face, direction === "'" ? -1 : 1, false);
                    }
                    
                    // Update visualizations
                    this.updateStateDisplay(newState);
                    this.update2DVisualization(newState);
                    
                    // Small delay between moves
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            } else {
                console.error('Failed to get solution:', data);
            }
        } catch (error) {
            console.error('Error solving cube:', error);
        } finally {
            this.isAnimating = false;
        }
    }

    updateStateDisplay(state) {
        // Format the state string into a more readable format
        const faces = ['Up', 'Right', 'Front', 'Down', 'Left', 'Back'];
        let formattedState = '';
        for (let i = 0; i < 6; i++) {
            formattedState += `${faces[i]}: ${state.slice(i * 9, (i + 1) * 9)}\n`;
        }
        this.stateDisplay.innerHTML = `<pre style="margin: 0; white-space: pre-wrap;">${formattedState}</pre>`;
    }

    create2DVisualization() {
        // Create container for 2D visualization
        this.viz2dContainer = document.createElement('div');
        this.viz2dContainer.className = 'viz-2d-container';
        this.viz2dContainer.style.position = 'fixed';
        this.viz2dContainer.style.left = '20px';
        this.viz2dContainer.style.top = '20px';
        this.viz2dContainer.style.zIndex = '2';
        this.viz2dContainer.style.display = 'none';
        this.viz2dContainer.style.transition = 'all 0.3s ease';
        this.viz2dContainer.style.transformOrigin = 'top left';
        this.viz2dContainer.style.opacity = '0';
        this.container.appendChild(this.viz2dContainer);
        
        // Initial update
        this.update2DVisualization(this.faceletCube.getFaceletString());
    }

    update2DVisualization(state) {
        // Clear existing visualization
        this.viz2dContainer.innerHTML = '';
        
        // Create new visualization
        const canvas = visualizeCube(state);
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
        this.viz2dContainer.appendChild(canvas);
    }

    create3DVisualization() {
        // Create container for 3D visualization
        this.viz3dContainer = document.createElement('div');
        this.viz3dContainer.style.position = 'absolute';
        this.viz3dContainer.style.width = '100%';
        this.viz3dContainer.style.height = '100%';
        this.viz3dContainer.style.zIndex = '1';
        this.container.appendChild(this.viz3dContainer);
        
        // Create renderer
        this.renderer = new RubiksCubeRenderer(this.viz3dContainer);
        
        // Start animation loop
        this.renderer.animate();
        
        // Initial update
        this.update3DVisualization(this.faceletCube.getFaceletString());
    }

    async update3DVisualization(state) {
        if (this.renderer) {
            await this.renderer.updateCubeState(state);
        } else {
            console.error('3D renderer not initialized');
        }
    }

    createQuestionMarkButton() {
        const questionMarkButton = document.createElement('button');
        questionMarkButton.textContent = '?';
        questionMarkButton.className = 'question-mark-button';
        questionMarkButton.style.position = 'fixed';
        questionMarkButton.style.top = '20px';
        questionMarkButton.style.left = '20px';
        questionMarkButton.style.width = '40px';
        questionMarkButton.style.height = '40px';
        questionMarkButton.style.fontSize = '24px';
        questionMarkButton.style.cursor = 'pointer';
        questionMarkButton.style.backgroundColor = '#2196F3';
        questionMarkButton.style.color = 'white';
        questionMarkButton.style.border = 'none';
        questionMarkButton.style.borderRadius = '50%';
        questionMarkButton.style.display = 'flex';
        questionMarkButton.style.alignItems = 'center';
        questionMarkButton.style.justifyContent = 'center';
        questionMarkButton.style.zIndex = '1000';
        questionMarkButton.style.transition = 'all 0.2s ease';
        questionMarkButton.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';

        // Add hover effect
        questionMarkButton.addEventListener('mouseenter', () => {
            questionMarkButton.style.transform = 'scale(1.1)';
            questionMarkButton.style.backgroundColor = '#1976D2';
        });

        questionMarkButton.addEventListener('mouseleave', () => {
            questionMarkButton.style.transform = 'scale(1)';
            questionMarkButton.style.backgroundColor = '#2196F3';
        });

        // Add click handler
        questionMarkButton.addEventListener('click', () => {
            this.toggleDebugInfo();
        });

        this.container.appendChild(questionMarkButton);

        // Add responsive styles for the question mark button
        const style = document.createElement('style');
        style.textContent = `
            @media (max-width: 768px) {
                .question-mark-button {
                    top: 10px !important;
                    left: 10px !important;
                    width: 35px !important;
                    height: 35px !important;
                    font-size: 20px !important;
                }
            }
            @media (max-width: 480px) {
                .question-mark-button {
                    top: 5px !important;
                    left: 5px !important;
                    width: 30px !important;
                    height: 30px !important;
                    font-size: 18px !important;
                }
            }
            @media (max-width: 320px) {
                .question-mark-button {
                    top: 2px !important;
                    left: 2px !important;
                    width: 25px !important;
                    height: 25px !important;
                    font-size: 16px !important;
                }
            }
        `;
        document.head.appendChild(style);
    }

    addResponsiveStyles() {
        const style = document.createElement('style');
        style.textContent = `
            @media (max-width: 768px) {
                .cube-button {
                    width: 35px !important;
                    height: 35px !important;
                    font-size: 14px !important;
                }
                .control-panel {
                    padding: 5px !important;
                    gap: 5px !important;
                }
                .action-button {
                    padding: 6px 12px !important;
                    font-size: 14px !important;
                }
                .state-display {
                    top: 10px !important;
                    right: 10px !important;
                    padding: 5px !important;
                    font-size: 12px !important;
                }
                .viz-2d-container {
                    left: 10px !important;
                    top: 10px !important;
                    transform: scale(0.8) !important;
                }
            }
            @media (max-width: 480px) {
                .cube-button {
                    width: 30px !important;
                    height: 30px !important;
                    font-size: 12px !important;
                }
                .control-panel {
                    padding: 3px !important;
                    gap: 3px !important;
                }
                .action-button {
                    padding: 4px 8px !important;
                    font-size: 12px !important;
                }
                .state-display {
                    top: 5px !important;
                    right: 5px !important;
                    padding: 3px !important;
                    font-size: 10px !important;
                }
                .viz-2d-container {
                    left: 5px !important;
                    top: 5px !important;
                    transform: scale(0.6) !important;
                }
            }
            @media (max-width: 320px) {
                .cube-button {
                    width: 25px !important;
                    height: 25px !important;
                    font-size: 10px !important;
                }
                .control-panel {
                    padding: 2px !important;
                    gap: 2px !important;
                }
                .action-button {
                    padding: 3px 6px !important;
                    font-size: 10px !important;
                }
                .state-display {
                    top: 2px !important;
                    right: 2px !important;
                    padding: 2px !important;
                    font-size: 8px !important;
                }
                .viz-2d-container {
                    left: 2px !important;
                    top: 2px !important;
                    transform: scale(0.4) !important;
                }
            }
        `;
        document.head.appendChild(style);
    }

    handleResize() {
        // Update panel position if needed
        const panelRect = this.panel.getBoundingClientRect();
        if (panelRect.bottom > window.innerHeight) {
            this.panel.style.bottom = '10px';
        }
    }

    setupKeyboardControls() {
        document.addEventListener('keydown', (event) => {
            if (event.key.toLowerCase() === 'f') {
                this.toggleDebugInfo();
            }
        });
    }

    toggleDebugInfo() {
        this.showDebugInfo = !this.showDebugInfo;
        this.stateDisplay.style.display = this.showDebugInfo ? 'block' : 'none';
        this.viz2dContainer.style.display = this.showDebugInfo ? 'block' : 'none';
        
        // Add transition effect
        if (this.showDebugInfo) {
            this.stateDisplay.style.opacity = '0';
            this.viz2dContainer.style.opacity = '0';
            setTimeout(() => {
                this.stateDisplay.style.opacity = '1';
                this.viz2dContainer.style.opacity = '1';
            }, 50);
        } else {
            this.stateDisplay.style.opacity = '0';
            this.viz2dContainer.style.opacity = '0';
        }
    }

    createMoveButtons() {
        const moves = ['U', 'R', 'F', 'D', 'L', 'B'];
        const directions = [
            { suffix: '', label: '↻' },      // Clockwise
            { suffix: "'", label: '↺' },     // Counterclockwise
            { suffix: '2', label: '2' }      // Double turn
        ];

        moves.forEach(face => {
            const faceDiv = document.createElement('div');
            faceDiv.style.display = 'flex';
            faceDiv.style.flexDirection = 'column';
            faceDiv.style.gap = '2px';

            directions.forEach(({ suffix, label }) => {
                const button = this.createButton(face, suffix, label);
                faceDiv.appendChild(button);
            });

            this.moveButtonsContainer.appendChild(faceDiv);
        });
    }

    createButton(face, suffix, label) {
        const button = document.createElement('button');
        button.textContent = face + label;
        button.className = 'cube-button';
        button.style.width = '40px';
        button.style.height = '40px';
        button.style.fontSize = '16px';
        button.style.cursor = 'pointer';
        button.style.backgroundColor = '#4CAF50';
        button.style.color = 'white';
        button.style.border = 'none';
        button.style.borderRadius = '5px';
        button.style.transition = 'all 0.2s ease';
        button.style.touchAction = 'manipulation'; // Optimize for touch devices

        // Add hover effect
        button.addEventListener('mouseenter', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1.1)';
                button.style.backgroundColor = '#45a049';
            }
        });

        button.addEventListener('mouseleave', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1)';
                button.style.backgroundColor = '#4CAF50';
            }
        });

        // Add touch feedback
        button.addEventListener('touchstart', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(0.95)';
            }
        });

        button.addEventListener('touchend', () => {
            if (!this.isAnimating) {
                button.style.transform = 'scale(1)';
                button.style.backgroundColor = '#4CAF50';
            }
        });

        button.addEventListener('click', async () => {
            if (this.isAnimating) {
                console.log('Move blocked: Animation in progress');
                return;
            }

            try {
                this.isAnimating = true;
                button.style.backgroundColor = '#cccccc';
                button.style.cursor = 'not-allowed';
                button.style.transform = 'scale(1)';
                
                const move = face + suffix;
                console.log(`Applying move: ${move}`);
                
                // Record the move in history
                this.moveHistory.push(move);
                
                // First update internal state
                this.faceletCube.applyMove(move);
                const newState = this.faceletCube.getFaceletString();
                
                // Store new state in renderer
                this.renderer.currentState = newState;
                
                // Execute rotation animation
                if (suffix === '2') {
                    await this.renderer.rotateFace(face, 1, true);
                } else {
                    await this.renderer.rotateFace(face, suffix === "'" ? -1 : 1, false);
                }
                
                // Update other visualizations
                this.updateStateDisplay(newState);
                this.update2DVisualization(newState);
                
                button.style.backgroundColor = '#45a049';
                setTimeout(() => {
                    button.style.backgroundColor = '#4CAF50';
                }, 200);
            } catch (error) {
                console.error('Error applying move:', error);
                button.style.backgroundColor = '#ff4444';
                setTimeout(() => {
                    button.style.backgroundColor = '#4CAF50';
                }, 500);
            } finally {
                this.isAnimating = false;
                button.style.cursor = 'pointer';
            }
        });

        return button;
    }

    createStateDisplay() {
        this.stateDisplay = document.createElement('div');
        this.stateDisplay.className = 'state-display';
        this.stateDisplay.style.position = 'fixed';
        this.stateDisplay.style.top = '20px';
        this.stateDisplay.style.right = '20px';
        this.stateDisplay.style.backgroundColor = '#f0f0f0';
        this.stateDisplay.style.padding = '10px';
        this.stateDisplay.style.borderRadius = '5px';
        this.stateDisplay.style.fontFamily = 'monospace';
        this.stateDisplay.style.zIndex = '2';
        this.stateDisplay.style.display = 'none';
        this.stateDisplay.style.transition = 'all 0.3s ease';
        this.stateDisplay.style.maxWidth = '200px';
        this.stateDisplay.style.overflow = 'auto';
        this.stateDisplay.style.maxHeight = '80vh';
        this.stateDisplay.style.opacity = '0';
        this.container.appendChild(this.stateDisplay);
        
        // Initial update
        this.updateStateDisplay(this.faceletCube.getFaceletString());
    }

    createSignature() {
        const signature = document.createElement('div');
        signature.textContent = 'by stingtao';
        signature.style.position = 'fixed';
        signature.style.bottom = '10px';
        signature.style.right = '10px';
        signature.style.fontFamily = "'Segoe UI', Arial, sans-serif";
        signature.style.fontSize = '14px';
        signature.style.color = '#666';
        signature.style.opacity = '0.8';
        signature.style.padding = '5px';
        signature.style.zIndex = '1000';
        signature.style.userSelect = 'none';
        signature.style.cursor = 'default';
        signature.style.textShadow = '1px 1px 2px rgba(255, 255, 255, 0.5)';
        
        // Add hover effect
        signature.addEventListener('mouseenter', () => {
            signature.style.opacity = '1';
            signature.style.transform = 'scale(1.1)';
            signature.style.transition = 'all 0.3s ease';
        });
        
        signature.addEventListener('mouseleave', () => {
            signature.style.opacity = '0.8';
            signature.style.transform = 'scale(1)';
        });

        this.container.appendChild(signature);
    }
} 